import {
  compactActions,
  dropRedundantChangesFromStoredProposalActions,
} from './clean-actions.helpers';
import { upgradeLegacyRecipeChanges } from './legacy-recipe-change.helpers';
import { isProjectChangeExcludedFromProposal } from './apply-project-changes-with-proposal-context.helpers';
import { deactivateStaleProposalActionChanges } from '@shared/validation/proposals-validation';
import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import type { Baseline } from '@shared/models/project.model';

/**
 * Repairs a single stored change so it can live on a proposal {@link Action}.
 *
 * - Legacy recipe discriminants are upgraded to {@link ProjectChangeType.ElementRecipeApply}.
 * - Other baseline-only types are dropped.
 */
export const repairChangeForProposalActionStorage = (
  change: ProjectChange,
): ProjectChange | undefined => {
  const [upgraded] = upgradeLegacyRecipeChanges([change]);
  const candidate = upgraded ?? change;

  if (!isProjectChangeExcludedFromProposal(candidate)) return candidate;

  return undefined;
};

/**
 * Strips forbidden / legacy recipe rows from every {@link Baseline.actions} entry
 * so {@link isValidProposalsAndActions} can pass after load.
 */
export const sanitizeProposalActionChangesInVersion = (
  version: Baseline,
): Baseline => {
  const actions = version.actions;
  if (!actions?.length) return version;

  let anyChanged = false;
  const nextActions = actions.map((action) => {
    const nextChanges: ProjectChange[] = [];
    for (const change of action.changes) {
      const repaired = repairChangeForProposalActionStorage(change);
      if (repaired) nextChanges.push(repaired);
      if (repaired !== change) anyChanged = true;
    }
    if (
      nextChanges.length === action.changes.length &&
      nextChanges.every((c, i) => c === action.changes[i])
    ) {
      return action;
    }
    anyChanged = true;
    return { ...action, changes: nextChanges };
  });

  const repairedVersion = anyChanged
    ? compactActions({ ...version, actions: nextActions })
    : version;

  const withoutRedundant =
    dropRedundantChangesFromStoredProposalActions(repairedVersion);
  // Mark stale references inactive instead of dropping them — degradation on the
  // smallest unit with an audit trail until squash (DEV-3640)
  const { version: sanitized } =
    deactivateStaleProposalActionChanges(withoutRedundant);
  if (sanitized === repairedVersion) {
    return repairedVersion;
  }
  return compactActions(sanitized);
};
